import type { AppResponse } from '@app/common';

import { env } from '../config/env';
import { supabase } from '../lib/supabase';
import { notifySessionExpired } from '../lib/sessionExpiry';

/**
 * Thrown when the backend rejects the request's token, so call sites can tell a
 * dead session apart from a genuine API failure instead of matching on the message.
 */
export class SessionExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

// Backend sendFailure wraps errors as: { success: false, errors: { message, error, ... } }
// This helper extracts the best human-readable message from any error shape.
function extractErrorMessage(json: AppResponse<unknown>, httpStatus: number): string {
  const errors = (json as unknown as Record<string, unknown>).errors;
  if (errors && typeof errors === 'object') {
    const e = errors as Record<string, unknown>;
    if (typeof e.message === 'string' && e.message) return e.message;
    if (typeof e.error === 'string' && e.error) return e.error;
  }
  if (typeof json.message === 'string' && json.message) return json.message;
  if (typeof json.error === 'string' && json.error) return json.error;
  return `Request failed with status ${httpStatus}`;
}

export const apiFetch = async <T>(path: string, options?: RequestInit): Promise<T> => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...options,
  });

  const json: AppResponse<T> = await response.json();

  // `extractAuthUser` answers 401 for both a missing header and an expired token, and
  // either way the user has to sign in again. Tearing the session down here — the one
  // place every request passes through — keeps each call site from having to guess.
  if (response.status === 401) {
    notifySessionExpired();
    throw new SessionExpiredError(extractErrorMessage(json, response.status));
  }

  if (!response.ok || !json.success) {
    throw new Error(extractErrorMessage(json, response.status));
  }

  return json.data as T;
};
